<template>
  <div class="w-full mb-8">
    <div class="flex items-center justify-between w-full mx-auto">
      <div class="w-1/3">
        <router-link
          :to="`/events/${eventType}`"
          class="mb-0 p-2 px-6 rounded-full bg-green text-white font-bold no-underline"
        >
          Add Event
        </router-link>
        <router-link
          v-if="eventType === 'liveclass'"
          to="/events/bulk-upload"
          class="mb-0 ml-4 p-2 px-6 rounded-full bg-purple-dark text-white font-bold no-underline"
        >
          Liveclass Bulk Upload
        </router-link>
      </div>
      <h2 class="w-1/3">Events - {{ eventType }}</h2>
      <div class="flex items-center">
        <p class="inline mr-4">Filters</p>
        <input id="active-only" v-model="active_only" type="checkbox" />
        <label for="active-only" class="p-0 ml-1">Active</label>
      </div>
    </div>
    <div
      class="flex w-1/2 mt-4 m-auto rounded-sm shadow-md sticky z-10 bg-white"
      style="top: 10px;"
    >
      <button
        v-for="tab in ['List', 'Calendar']"
        :key="tab"
        class="w-1/2 text-center tab rounded-none"
        :class="`${activeTab === tab ? 'tab-active' : ''}`"
        @click="changeTab(tab)"
      >
        {{ tab }}
      </button>
    </div>
    <div
      v-if="activeTab === 'Calendar'"
      style="max-width: 1000px"
      class="m-auto mt-4 fadeIn"
    >
      <FullCalendar
        ref="calendar"
        :options="calendarOptions"
        class="text-white w-full calendar shadow-lg rounded-lg text-base h-full event-calendar p-2"
      />
    </div>
    <div v-show="activeTab === 'List'" class="fadeIn shadow-md mt-4 w-full">
      <Table
        v-if="eventType === 'liveclass'"
        ref="eventTable"
        :endpoint="endpoint"
        :tableConfig="tableConfigLiveclass"
      />
      <Table
        v-else-if="eventType === 'contest'"
        ref="eventTable"
        :endpoint="endpoint"
        :tableConfig="tableConfigContest"
      />
    </div>
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue"
import dayGridPlugin from "@fullcalendar/daygrid"
import listPlugin from "@fullcalendar/list"
import { format } from "date-fns"
import siteConfig from "@/utils/siteConfig"
import copy from "@/utils/copy"
import Table from "@/components/DataTable/Table"
import { archiveLiveclass } from "@/apis/events/index"

export default {
  components: {
    FullCalendar,
    Table,
  },
  data() {
    return {
      eventType: "",
      active_only: false,
      events: [],
      activeTab: "List",
    }
  },
  computed: {
    endpoint() {
      return `${siteConfig.siteURL}events/?active_only=${
        this.active_only ? 1 : 0
      }&event_type=${this.eventType}`
    },
    tableConfigLiveclass() {
      return [
        {
          header: { textContent: "Event Name" },
          path: "event_name",
        },
        { header: { textContent: "Slug" }, path: "slug" },
        {
          header: { textContent: "Offline URL" },
          path: "watch_offline_url",
          formatter: this.getOfflineUrl,
        },
        {
          header: { textContent: "Event Id" },
          path: "/",
          formatter: ({
            event_key_id,
            zoom_account_email,
            liveclass_provider,
          }) =>
            this.getLiveClassProvider(
              liveclass_provider,
              event_key_id,
              zoom_account_email
            ),
        },
        {
          header: { textContent: "Liveclass Provider" },
          path: "liveclass_provider",
        },
        {
          header: { textContent: "Start Date" },
          type: "date",
          path: "start_date",
          formatter: this.dateformat,
        },
        {
          header: { textContent: "End Date" },
          type: "date",
          path: "end_date",
          formatter: this.dateformat,
        },
        {
          header: {
            textContent: "Actions",
          },
          path: "id",
          type: "action",
          actionButtonText: "Edit",
          routePath: `/events/${this.eventType}/:id`,
        },
        {
          header: { textContent: "Archive" },
          path: "/",
          type: "button",
          classList: "rounded-sm m-0 edit-button",
          buttonText: `Archive`,
          onclick: ({ id, event_name }) => {
            this.deleteClass(id, event_name)
          },
        },
      ]
    },
    tableConfigContest() {
      return [
        {
          header: { textContent: "Event Name" },
          path: "event_name",
        },
        { header: { textContent: "Slug" }, path: "slug" },
        {
          header: { textContent: "Event Id" },
          path: "event_key_id",
        },
        {
          header: { textContent: "Start Date" },
          type: "date",
          path: "start_date",
        },
        {
          header: { textContent: "End Date" },
          type: "date",
          path: "end_date",
        },
        {
          header: {
            textContent: "Actions",
          },
          path: "id",
          type: "action",
          actionButtonText: "Edit",
          routePath: `/events/${this.eventType}/:id`,
        },
      ]
    },
    tasks() {
      return this.events.map(item => ({
        title: item.event_name,
        start: new Date(item.start_date).toISOString(),
        end: new Date(item.end_date).toISOString(),
        id: item.id,
        url: `/events/${this.eventType}/${item.id}`,
      }))
    },
    calendarOptions() {
      return {
        plugins: [dayGridPlugin, listPlugin],
        dayMaxEvents: true,
        events: this.tasks,
        eventDisplay: "block",
        eventOrder: "start",
        titleFormat: { year: "numeric", month: "long" },
        eventTimeFormat: {
          meridiem: "short",
          hour: "numeric",
          minute: "numeric",
          hour12: true,
          omitZeroMinute: true,
        },
        headerToolbar: {
          start: "prev,next today",
          center: "title",
          end: "dayGridMonth,dayGridWeek,listWeek",
        },
        buttonText: {
          today: "Today",
          month: "Month",
          week: "Week",
          day: "Day",
          list: "List",
        },
        eventClick: this.goToEditPage,
        displayEventEnd: true,
      }
    },
  },
  watch: {
    active_only() {
      this.fetchEvents()
    },
    $route() {
      this.eventType =
        this.$route.path === "/event-list/liveclass" ? "liveclass" : "contest"
      this.fetchEvents()
    },
  },
  created() {
    if (this.$route.query && this.$route.query.tab) {
      this.activeTab = this.$route.query.tab
    } else {
      this.activeTab = "List"
      this.updateRouteWithTab()
    }
    this.eventType =
      this.$route.path === "/event-list/liveclass" ? "liveclass" : "contest"
    this.fetchEvents()
  },
  mounted() {
    // for the sake of our data table component
    window.copyLink = this.copyLink
  },
  beforeDestroy() {
    // remove the function from window
    Reflect.deleteProperty(window, "copyLink")
  },
  methods: {
    async deleteClass(id, name) {
      const value = await this.$swal({
        icon: "warning",
        text: `Are you sure you want to delete liveclass: ${name}`,
        buttons: {
          cancel: true,
          confirm: {
            text: "Archive",
            value: true,
          },
        },
      })
      if (value) {
        archiveLiveclass(id)
          .then(res => {
            this.$noty.info(res.data.message)
          })
          .catch(err => {
            console.error(err)
            let message = "Failed to archive liveclass"
            if (err.response) {
              message = err.response.data.message
            }
            this.$noty.error(message)
          })
          .finally(() => {
            this.$refs.eventTable.getItems()
          })
      }
    },
    dateformat(dateStr) {
      const d = new Date(dateStr)
      let datefmt = "dd MMM"
      let timefmt = "h:mm bbb"
      if (d.getFullYear() !== new Date().getFullYear()) {
        datefmt = "dd/MMM/yyyy"
      }
      if (d.getMinutes() === 0) {
        timefmt = "h bbb"
      }

      return format(d, `${datefmt} (${timefmt})`)
    },
    changeTab(tab) {
      if (tab === this.activeTab) return
      this.activeTab = tab
      this.updateRouteWithTab()

      if (tab === "Calendar" && !this.calendar) {
        this.$nextTick(() => {
          this.calendar = this.$refs.calendar.getApi()
        })
      }
    },
    updateRouteWithTab() {
      const routeObj = { ...this.$route, query: { ...this.$route.query } }
      routeObj.query.tab = this.activeTab
      this.$router.replace(routeObj).catch(() => {})
    },
    goToEditPage(info) {
      /* manual implementation of router-link behavior */
      if (!info.jsEvent.ctrlKey) {
        info.jsEvent.preventDefault()
        this.$router.push({ path: info.event.url })
      }
    },
    getEditLink(event) {
      return `/events/${this.eventType}/${event.id}`
    },
    copyLink(text) {
      copy(text)
      this.$noty.info("Copied!", {
        layout: "topRight",
        theme: "mint",
        timeout: 3,
      })
    },
    fetchEvents() {
      this.axios({
        method: "GET",
        url: `${siteConfig.siteURL}events/?active_only=${
          this.active_only ? 1 : 0
        }&event_type=${this.eventType}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        this.events = res.data.items
      })
    },
    getOfflineUrl(watch_offline_url) {
      if (watch_offline_url) {
        return `<span v-show="event.watch_offline_url" class="flex items-start">
            <button
              title="copy"
              class="copy-btn hover:bg-grey-lightest active:bg-grey-lightest focus:bg-grey-lightest rounded-sm mr-1"
              onclick="copyLink('${watch_offline_url}')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="#3490dc"
                style="width: 1.5rem; height: 1.5rem;"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="{2}"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </button>
            <a href="${watch_offline_url}" target="_blank">${watch_offline_url}</a>
          </span>`
      }
      return null
    },
    getLiveClassProvider(liveclass_provider, event_key_id, zoom_account_email) {
      let style = ""
      if (liveclass_provider === "zoom") {
        if (
          zoom_account_email === "aneeq@edyst.com" ||
          zoom_account_email === "msrinivasa@edyst.com"
        )
          style =
            "width: max-content; margin-top: 7px;padding: 4px;border-radius: 8px;background: lightblue;font-size: 0.8rem;"
        else style = "width: max-contereground: ;font-size: 0.8rem;"
        return `${event_key_id}
          <div style="${style}">${zoom_account_email}</div>`
      }
      return `${event_key_id}`
    },
  },
}
</script>

<style scoped>
input[type="checkbox"] {
  width: 20px;
}

.event-row td {
  border-bottom: 1px solid silver;
}

.event-row:hover {
  filter: brightness(0.8);
}

.copy-btn {
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
}
</style>

<style>
.event-calendar.fc table {
  border-collapse: separate !important;
}
.event-calendar .fc-h-event .fc-event-time {
  overflow: visible;
}
.event-calendar table td {
  background: white;
}

.event-calendar .fc .fc-daygrid-day.fc-day-today {
  background-color: #fffadf;
}

.event-calendar .fc-daygrid-day-events div {
  color: white;
}

.tab-active {
  color: #ffffff;
  background-color: #3490dc;
}
</style>
